import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Markdown preview — lightweight render of LLM-style text content
// inside a bounded box. Used by both DagNodeHoverContent (richer
// hovers) and the synthesis llm_round node's at-a-glance answer
// preview. No syntax highlighting (keeps the bundle quiet); just
// GFM markdown so **bold**, *italic*, lists, links, blockquotes,
// inline + fenced code blocks, and tables all read correctly.
//
// Vertical clip is via max-height because line-clamp doesn't
// compose well with multi-block children (paragraphs, lists, etc).
export interface MarkdownPreviewProps {
  text: string;
  /** Approximate visible lines. Translates to a max-height box. */
  maxLines?: number;
  /** Override the base text size for callers that need a denser feel
   *  (e.g. the synth round's at-a-glance answer preview). Defaults to
   *  text-[11px] which matches the hover surface. */
  textSizeClass?: string;
}

export const MarkdownPreview: React.FC<MarkdownPreviewProps> = ({
  text,
  maxLines = 8,
  textSizeClass = 'text-[11px]',
}) => {
  // ~1.5em line-height assumption; pad a touch for paragraph margins.
  const maxHeight = `${Math.max(2, maxLines) * 1.55}em`;
  return (
    <div
      className={`${textSizeClass} leading-snug break-words overflow-hidden chatmap-md-preview`}
      style={{ maxHeight }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Tighten spacing across the board so the preview doesn't
          // burn vertical real estate on margins.
          p: ({ node, children }) => <p className="my-0.5">{children}</p>,
          ul: ({ node, children }) => <ul className="my-0.5 pl-4 list-disc">{children}</ul>,
          ol: ({ node, children }) => <ol className="my-0.5 pl-4 list-decimal">{children}</ol>,
          li: ({ node, children }) => <li className="my-0">{children}</li>,
          h1: ({ node, children }) => <strong className="block my-0.5">{children}</strong>,
          h2: ({ node, children }) => <strong className="block my-0.5">{children}</strong>,
          h3: ({ node, children }) => <strong className="block my-0.5">{children}</strong>,
          h4: ({ node, children }) => <strong className="block my-0.5">{children}</strong>,
          blockquote: ({ node, children }) => (
            <blockquote className="border-l-2 border-current/30 pl-2 my-0.5 opacity-90">
              {children}
            </blockquote>
          ),
          code: ({ node, inline, className, children, ...props }: any) =>
            inline ? (
              <code
                className="px-1 py-px rounded bg-black/15 dark:bg-white/10 text-[10px] font-mono"
                {...props}
              >
                {children}
              </code>
            ) : (
              <pre
                className="my-0.5 p-1.5 rounded bg-black/20 dark:bg-white/5 overflow-hidden text-[10px] font-mono whitespace-pre-wrap"
                {...props}
              >
                <code>{children}</code>
              </pre>
            ),
          a: ({ node, href, children, ...props }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="underline decoration-current/40 hover:decoration-current"
              {...props}
            >
              {children}
            </a>
          ),
          table: ({ node, children }) => (
            <table className="my-0.5 text-[10px] border-collapse">{children}</table>
          ),
          th: ({ node, children }) => (
            <th className="border border-current/20 px-1 py-0.5 text-left font-semibold">
              {children}
            </th>
          ),
          td: ({ node, children }) => (
            <td className="border border-current/20 px-1 py-0.5">{children}</td>
          ),
          hr: () => <hr className="my-1 border-current/20" />,
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownPreview;
